import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ArrowLeft, Gavel, Lock } from 'lucide-react';
import { PageShell } from '@/components/common/PageShell';
import { Button, Card, Combobox, Select, type ComboboxOption } from '@/components/ui';
import {
  getGrievanceCategories,
  searchGrievanceColleagues,
  submitGrievance,
  type SubmitGrievanceInput,
} from '@/api/services/hrCases.service';
import { getMyCompanies } from '@/api/services/companies.service';
import { getErrorMessage } from '@/lib/getErrorMessage';
import { FormShell, TextArea } from '../cases/caseFields';
import { LABEL_CLASS } from '../cases/caseFormat';

function colleagueSearch(companyId: string) {
  return async (query: string): Promise<ComboboxOption[]> => {
    const people = await searchGrievanceColleagues(companyId, query.trim());
    return (Array.isArray(people) ? people : []).map((person) => ({
      value: person.userId,
      label: person.employeeId ? `${person.name} · ${person.employeeId}` : person.name,
    }));
  };
}

/**
 * Raises a confidential grievance. It goes to the company's grievance
 * handlers, or to alternates when a handler is involved; only those people
 * and the employee can see it.
 */
export function RaiseGrievancePage(): React.ReactElement {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // One id per open form, so a retried submission is not raised twice.
  const [requestId] = useState(() => crypto.randomUUID());
  const [pickedCompanyId, setCompanyId] = useState('');
  const [categoryKey, setCategoryKey] = useState('');
  const [respondentUserId, setRespondentUserId] = useState('');
  const [respondentIsHr, setRespondentIsHr] = useState(false);
  const [description, setDescription] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);

  const companies = useQuery({ queryKey: ['me', 'companies'], queryFn: getMyCompanies });
  const companyList = Array.isArray(companies.data) ? companies.data : [];
  const companyId = pickedCompanyId || (companyList.length === 1 ? companyList[0]._id : '');
  const categories = useQuery({
    queryKey: ['grievance-categories', companyId],
    queryFn: () => getGrievanceCategories(companyId),
    enabled: companyId !== '',
  });
  const categoryList = Array.isArray(categories.data) ? categories.data : [];

  const submit = useMutation({
    mutationFn: (input: SubmitGrievanceInput) => submitGrievance(input),
    onSuccess: (raised) => {
      toast.success('Grievance submitted');
      void queryClient.invalidateQueries({ queryKey: ['my-hr-cases'] });
      navigate(`/my-hr-cases/${raised._id}`);
    },
    onError: (mutationError) => setError(getErrorMessage(mutationError)),
  });
  const ready = companyId !== '' && categoryKey !== '' && description.trim() !== '';

  return (
    <PageShell
      breadcrumbItems={[
        { label: 'Self Service', to: '/dashboard' },
        { label: 'My HR Cases', to: '/my-hr-cases' },
        { label: 'Raise a grievance' },
      ]}
      icon={Gavel}
      title="Raise a grievance"
      className="gap-0"
      hideHeader
    >
      <div className="mx-auto w-full max-w-3xl space-y-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/my-hr-cases')}>
          <ArrowLeft className="mr-1.5 h-4 w-4" aria-hidden />
          My HR Cases
        </Button>
        <Card>
          <FormShell
            title="Raise a grievance"
            error={error}
            onCancel={() => navigate('/my-hr-cases')}
            onSubmit={() => {
              setError(null);
              submit.mutate({
                requestId,
                companyId,
                categoryKey,
                ...(respondentUserId && { respondentUserId }),
                respondentIsHr,
                description: description.trim(),
                ...(file && { file }),
              });
            }}
            submitLabel="Submit grievance"
            disabled={!ready}
            isPending={submit.isPending}
          >
            <p className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-300">
              <Lock className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
              Only the people handling your grievance can see it. If it concerns someone who
              normally handles grievances, it goes to other handlers instead. Your name is shared
              with the handlers.
            </p>
            {companies.error && (
              <p className="text-sm text-danger" role="alert">
                {getErrorMessage(companies.error)}
              </p>
            )}
            <div className="grid gap-4 sm:grid-cols-2">
              {companyList.length > 1 && (
                <Select
                  id="grievance-company"
                  label="Company"
                  value={companyId}
                  onChange={(value) => {
                    setCompanyId(value);
                    setCategoryKey('');
                    setRespondentUserId('');
                  }}
                  options={companyList.map((company) => ({
                    value: company._id,
                    label: company.name,
                  }))}
                  placeholder="Choose a company"
                  clearable={false}
                />
              )}
              <Select
                id="grievance-category"
                label="Category"
                value={categoryKey}
                onChange={setCategoryKey}
                options={categoryList.map((category) => ({
                  value: category.key,
                  label: category.label,
                }))}
                placeholder={
                  companyId === ''
                    ? 'Choose a company first'
                    : categories.isLoading
                      ? 'Loading categories…'
                      : 'Choose a category'
                }
                clearable={false}
              />
            </div>
            {companyId !== '' && (
              <Combobox
                key={companyId}
                id="grievance-respondent"
                label="Who is it about? (optional)"
                placeholder="Search by name or employee ID"
                value={respondentUserId}
                onChange={setRespondentUserId}
                loadOptions={colleagueSearch(companyId)}
              />
            )}
            <label className="flex items-start gap-2 text-sm text-gray-800 dark:text-gray-200">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={respondentIsHr}
                onChange={(event) => setRespondentIsHr(event.target.checked)}
              />
              It concerns HR, so it should not go to the usual HR handlers
            </label>
            <TextArea
              id="grievance-description"
              label="What happened"
              value={description}
              onChange={setDescription}
              required
              rows={6}
              hint="Include dates, places, and anyone who saw it. You can add more later."
            />
            <div>
              <label htmlFor="grievance-file" className={LABEL_CLASS}>
                Attachment (optional)
              </label>
              <input
                id="grievance-file"
                type="file"
                accept=".pdf,.png,.jpg,.jpeg,.webp,.txt,.doc,.docx,.xlsx"
                onChange={(event) => setFile(event.target.files?.[0] ?? null)}
                className="block w-full text-sm text-gray-700 file:mr-3 file:rounded-lg file:border-0 file:bg-gray-100 file:px-3 file:py-2 file:text-sm file:font-medium dark:text-gray-200 dark:file:bg-gray-700"
                aria-describedby="grievance-file-hint"
              />
              <p
                id="grievance-file-hint"
                className="mt-1.5 text-xs text-gray-600 dark:text-gray-300"
              >
                PDF, image, text, Word, or Excel, up to 20 MB.
              </p>
            </div>
          </FormShell>
        </Card>
      </div>
    </PageShell>
  );
}
